import React, { useState, useEffect, useRef } from 'react';
import './AccessibilityMenu.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'ume-a11y-prefs';

const DEFAULTS = {
    largeFonts: false,
    highContrast: false,
    reduceMotion: false,
    tts: false,
};

// largeFonts goes on <html> so rem-based scaling cascades everywhere.
// All others go on <body>.
const HTML_CLASSES = {
    largeFonts: 'a11y-large-fonts',
};

const BODY_CLASSES = {
    highContrast:  'a11y-high-contrast',
    reduceMotion:  'a11y-reduce-motion',
};

// ─── TTS helpers ──────────────────────────────────────────────────────────────

let ttsActive = false;

const stopTTS = () => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
    }
    ttsActive = false;
};

const startTTS = () => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;

    const handleClick = (e) => {
        const el = e.target.closest(
            'p, h1, h2, h3, h4, h5, h6, li, label, span, button, a, td, th'
        );
        const text = el?.innerText?.trim();
        if (!text) return;

        window.speechSynthesis.cancel();
        const utt = new SpeechSynthesisUtterance(text);
        utt.rate = 0.95;
        utt.pitch = 1;
        window.speechSynthesis.speak(utt);
    };

    document.addEventListener('click', handleClick, { capture: true });
    ttsActive = true;

    // Store the handler so we can remove it later
    document._a11yTTSHandler = handleClick;
};

const teardownTTS = () => {
    stopTTS();
    if (document._a11yTTSHandler) {
        document.removeEventListener('click', document._a11yTTSHandler, { capture: true });
        document._a11yTTSHandler = null;
    }
};

// ─── Persist / restore prefs ──────────────────────────────────────────────────

const loadPrefs = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
    } catch {
        return { ...DEFAULTS };
    }
};

const savePrefs = (prefs) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch {}
};

const applyBodyClasses = (prefs) => {
    Object.entries(HTML_CLASSES).forEach(([key, cls]) => {
        document.documentElement.classList.toggle(cls, Boolean(prefs[key]));
    });
    Object.entries(BODY_CLASSES).forEach(([key, cls]) => {
        document.body.classList.toggle(cls, Boolean(prefs[key]));
    });
};

// ─── Component ────────────────────────────────────────────────────────────────

const AccessibilityMenu = () => {
    const [open, setOpen] = useState(false);
    const [prefs, setPrefs] = useState(DEFAULTS);
    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    // Load saved prefs on mount
    useEffect(() => {
        const saved = loadPrefs();
        setPrefs(saved);
        applyBodyClasses(saved);
        if (saved.tts) startTTS();
    }, []);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); } };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open]);

    const toggle = (key) => {
        setPrefs((prev) => {
            const next = { ...prev, [key]: !prev[key] };

            // Apply classes — largeFonts on <html>, rest on <body>
            if (key in HTML_CLASSES) {
                document.documentElement.classList.toggle(HTML_CLASSES[key], next[key]);
            } else if (key in BODY_CLASSES) {
                document.body.classList.toggle(BODY_CLASSES[key], next[key]);
            }

            // TTS side effects
            if (key === 'tts') {
                next.tts ? startTTS() : teardownTTS();
            }

            savePrefs(next);
            return next;
        });
    };

    const resetAll = () => {
        teardownTTS();
        applyBodyClasses(DEFAULTS);
        setPrefs({ ...DEFAULTS });
        savePrefs(DEFAULTS);
    };

    const anyActive = Object.values(prefs).some(Boolean);

    const options = [
        {
            key: 'largeFonts',
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <text x="2" y="18" fontSize="14" fontWeight="bold" stroke="none" fill="currentColor">A</text>
                    <text x="11" y="20" fontSize="18" fontWeight="bold" stroke="none" fill="currentColor">A</text>
                </svg>
            ),
            label: 'Larger text',
            desc: 'Increases font size across the page',
        },
        {
            key: 'highContrast',
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 3v18" />
                    <path d="M12 3a9 9 0 010 18z" fill="currentColor" stroke="none" />
                </svg>
            ),
            label: 'High contrast',
            desc: 'Increases colour contrast for readability',
        },

        {
            key: 'reduceMotion',
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                </svg>
            ),
            label: 'Reduce motion',
            desc: 'Turns off animations and transitions',
        },
        {
            key: 'tts',
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M11 5L6 9H2v6h4l5 4V5z" />
                    <path d="M19.07 4.93a10 10 0 010 14.14" />
                    <path d="M15.54 8.46a5 5 0 010 7.07" />
                </svg>
            ),
            label: 'Text-to-speech',
            desc: 'Click any text on the page to hear it read aloud',
        },
    ];

    return (
        <div className="a11y-widget" ref={menuRef}>
            {/* Trigger button */}
            <button
                ref={buttonRef}
                className={`a11y-trigger ${anyActive ? 'a11y-trigger--active' : ''}`}
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-haspopup="true"
                aria-label="Accessibility options"
                title="Accessibility options"
            >
                {/* Person / accessibility icon */}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="5" r="2" />
                    <path d="M12 7v7" />
                    <path d="M8 10h8" />
                    <path d="M9 21l3-4 3 4" />
                </svg>
                {anyActive && <span className="a11y-badge" aria-hidden="true" />}
            </button>

            {/* Dropdown panel */}
            {open && (
                <div
                    className="a11y-panel"
                    role="dialog"
                    aria-label="Accessibility settings"
                >
                    <div className="a11y-panel-header">
                        <span className="a11y-panel-title">Accessibility</span>
                        {anyActive && (
                            <button className="a11y-reset" onClick={resetAll}>
                                Reset all
                            </button>
                        )}
                    </div>

                    <ul className="a11y-options" role="list">
                        {options.map(({ key, icon, label, desc }) => (
                            <li key={key}>
                                <button
                                    className={`a11y-option ${prefs[key] ? 'a11y-option--on' : ''}`}
                                    onClick={() => toggle(key)}
                                    aria-pressed={prefs[key]}
                                >
                                    <span className="a11y-option-icon">{icon}</span>
                                    <span className="a11y-option-copy">
                    <span className="a11y-option-label">{label}</span>
                    <span className="a11y-option-desc">{desc}</span>
                  </span>
                                    <span className="a11y-toggle" aria-hidden="true">
                    <span className="a11y-toggle-thumb" />
                  </span>
                                </button>
                            </li>
                        ))}
                    </ul>

                    {prefs.tts && (
                        <p className="a11y-tts-hint">
                            💬 Click any text on the page to hear it spoken aloud.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default AccessibilityMenu;